/**
 * Niveau Override Section (Epic 1 — Roltypes & niveau-bepaling v1, Story 1.3)
 *
 * Override-badge + dialoog voor het handmatig instellen/opheffen van een
 * medewerker-niveau, met verplichte reden. Uitgelicht als eigen component
 * (i.p.v. inline in `EmployeeDetailPage.tsx`, dat al zeer groot is) zodat
 * dit HR-gevoelige, zichtbare gedrag geïsoleerd getest kan worden — zie
 * `NiveauOverrideSection.test.tsx`.
 */
import { useState } from 'react';
import { useNiveaus } from './useEmployees';
import { useNiveauStatus, useSetNiveauOverride, useRemoveNiveauOverride } from './useEmployeeNiveauOverride';
import { useAuth } from '../auth/AuthContext';

interface NiveauOverrideSectionProps {
  employeeId: number;
  employeeNiveauName: string;
}

export function NiveauOverrideSection({ employeeId, employeeNiveauName }: NiveauOverrideSectionProps) {
  const { hasRole } = useAuth();
  const canManageNiveauOverride = hasRole(['admin', 'hr']);

  // Code-review fix: gated by canManageNiveauOverride — the backend route is
  // admin|hr-only, so firing it unconditionally would guarantee a 403 for
  // every beoordelaar/medewerker viewer.
  const { data: niveauStatus } = useNiveauStatus(employeeId, canManageNiveauOverride);
  const { data: allNiveaus, isLoading: niveausLoading } = useNiveaus();
  const setNiveauOverride = useSetNiveauOverride();
  const removeNiveauOverride = useRemoveNiveauOverride();

  const [showDialog, setShowDialog] = useState(false);
  const [overrideNiveauId, setOverrideNiveauId] = useState<number | ''>('');
  const [overrideReden, setOverrideReden] = useState('');

  function openDialog() {
    setOverrideNiveauId('');
    setOverrideReden('');
    setShowDialog(true);
  }

  async function handleSetNiveauOverride() {
    if (overrideNiveauId === '' || overrideReden.trim().length === 0) return;

    try {
      await setNiveauOverride.mutateAsync({
        employeeId,
        input: { niveauId: overrideNiveauId, reden: overrideReden.trim() },
      });
      setShowDialog(false);
      setOverrideNiveauId('');
      setOverrideReden('');
    } catch {
      // Error is rendered from mutation state (setNiveauOverride.error) below.
    }
  }

  async function handleRemoveNiveauOverride() {
    if (!window.confirm('Weet u zeker dat u de niveau-override wilt opheffen? Het niveau valt terug op de automatisch berekende waarde.')) {
      return;
    }

    try {
      await removeNiveauOverride.mutateAsync(employeeId);
    } catch {
      // Error is rendered from mutation state (removeNiveauOverride.isError) below.
    }
  }

  return (
    <>
      {niveauStatus?.niveauOverride ? (
        <>
          <span
            data-testid="niveau-override-badge"
            title={niveauStatus.overrideReden ?? undefined}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FCEFCB] text-[#8A6116]"
          >
            {employeeNiveauName} (handmatig ingesteld)
          </span>
          {canManageNiveauOverride && (
            <div className="mt-2">
              <button
                type="button"
                data-testid="niveau-override-remove-button"
                onClick={handleRemoveNiveauOverride}
                disabled={removeNiveauOverride.isPending}
                className="text-xs font-medium text-[#2F5A7A] hover:underline disabled:opacity-50"
              >
                {removeNiveauOverride.isPending ? 'Bezig...' : 'Override opheffen'}
              </button>
              {removeNiveauOverride.isError && (
                <p data-testid="niveau-override-remove-error" className="mt-1 text-xs text-[#D64545]">
                  Opheffen van de override is mislukt. Probeer het opnieuw.
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {employeeNiveauName}
          </span>
          <p className="mt-1 text-xs text-gray-500">Automatisch berekend op basis van toegewezen taken</p>
          {canManageNiveauOverride && (
            <div className="mt-2">
              <button
                type="button"
                data-testid="niveau-override-set-button"
                onClick={openDialog}
                className="text-xs font-medium text-[#54949E] hover:underline"
              >
                Niveau handmatig instellen
              </button>
            </div>
          )}
        </>
      )}

      {showDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          data-testid="niveau-override-dialog"
          aria-labelledby="niveau-override-dialog-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={() => {
                // Code-review fix: don't let the backdrop silently dismiss the
                // dialog while a save is in flight.
                if (!setNiveauOverride.isPending) setShowDialog(false);
              }}
            ></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <h3 id="niveau-override-dialog-title" className="text-lg font-medium text-gray-900 mb-4">
                Niveau handmatig instellen
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="niveau-override-niveau" className="block text-sm font-medium text-gray-700">
                    Niveau
                  </label>
                  <select
                    id="niveau-override-niveau"
                    data-testid="niveau-override-niveau-select"
                    value={overrideNiveauId}
                    onChange={(e) => setOverrideNiveauId(e.target.value ? Number(e.target.value) : '')}
                    disabled={niveausLoading}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm disabled:opacity-50"
                  >
                    <option value="">{niveausLoading ? 'Niveaus laden...' : 'Kies een niveau'}</option>
                    {/* Alle 6 niveaus zijn selecteerbaar voor een override — bewust
                        geen filter op minPunten/usesMarktcorrectie, in tegenstelling
                        tot de roltype-default-dropdowns uit Story 1.1/1.2. */}
                    {allNiveaus?.map((niveau) => (
                      <option key={niveau.id} value={niveau.id}>
                        {niveau.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="niveau-override-reden" className="block text-sm font-medium text-gray-700">
                    Reden voor afwijking
                  </label>
                  <textarea
                    id="niveau-override-reden"
                    data-testid="niveau-override-reden-input"
                    rows={3}
                    maxLength={500}
                    value={overrideReden}
                    onChange={(e) => setOverrideReden(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                    placeholder="Bijv. marktcorrectie-afspraak met directie"
                  />
                  <p className="mt-1 text-xs text-gray-500">{overrideReden.length}/500 tekens</p>
                </div>
              </div>

              {setNiveauOverride.error && (
                <p data-testid="niveau-override-set-error" className="mt-3 text-sm text-[#D64545]">
                  Fout bij het opslaan van de override. Probeer het opnieuw.
                </p>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDialog(false)}
                  disabled={setNiveauOverride.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  data-testid="niveau-override-submit-button"
                  onClick={handleSetNiveauOverride}
                  disabled={overrideNiveauId === '' || overrideReden.trim().length === 0 || setNiveauOverride.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63] disabled:opacity-50"
                >
                  {setNiveauOverride.isPending ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
